import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebase'; // Assuming auth is available for admin checks
import { 
    collection, 
    addDoc, 
    getDocs, 
    query, 
    orderBy, 
    deleteDoc, 
    doc, 
    updateDoc,
    Timestamp 
} from 'firebase/firestore';
import { FaPlusCircle, FaTrashAlt, FaToggleOn, FaToggleOff, FaEdit, FaSortDown, FaSortUp } from 'react-icons/fa';
import '../../styles/AdminNotifications.css'; // Assuming we'll create a simple CSS file

// --- Constants ---
// NOTE: Updated types to be more relevant to a personal finance app (Mango)
const NOTIFICATION_TYPES = [
    'New Feature', 
    'Update', 
    'User Query', // Changed from 'Client Message'
    'Diet Tip', // Changed from 'Diet Tip'
    'System Alert'
];

const NOTIFICATION_ICONS = {
    'New Feature': '✨',
    'Update': '🚀',
    'User Query': '💬', // Changed icon for 'User Query'
    'Diet Tip': '💡', // Changed icon for 'Finance Tip'
    'System Alert': '⚠️',
};

// --- Component Definition ---

const NotificationsManager = () => {
    // State for the form input
    const [newMessage, setNewMessage] = useState('');
    const [newType, setNewType] = useState(NOTIFICATION_TYPES[0]);
    
    // State for the list of notifications
    const [notifications, setNotifications] = useState([]);
    
    // UI state
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch notifications from Firebase
    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const notificationsRef = collection(db, 'appNotifications');
            // Fetch all notifications, ordered newest first
            const q = query(notificationsRef, orderBy('timestamp', 'desc')); 
            const querySnapshot = await getDocs(q);
            
            const fetchedNotifications = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firebase Timestamp for display
                timestamp: doc.data().timestamp?.toDate().toLocaleString() || 'N/A', 
            }));
            
            setNotifications(fetchedNotifications);
        } catch (err) {
            console.error("Error fetching notifications:", err);
            setError("Failed to load existing notifications.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Load data on component mount
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);


    // Handler to submit a new notification
    const handleAddNotification = async (e) => {
        e.preventDefault();
        
        if (!newMessage.trim()) {
            setError("Notification message cannot be empty.");
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            const notificationsRef = collection(db, 'appNotifications');
            
            await addDoc(notificationsRef, {
                message: newMessage.trim(),
                type: newType,
                icon: NOTIFICATION_ICONS[newType] || '💬',
                timestamp: Timestamp.now(), // Use Firebase Timestamp object
                isActive: true, // New notifications are active by default
                postedBy: auth.currentUser?.email || 'Admin User', // Log who posted it
            });

            setNewMessage(''); // Clear input
            setSuccess("Notification posted successfully!");
            fetchNotifications(); // Refresh the list
            
        } catch (err) {
            console.error("Error adding notification:", err);
            setError("Failed to post notification. Check console for details.");
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setSuccess(null), 3000); // Clear success message
        }
    };
    
    // Handler to delete a notification
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this notification?")) return;
        
        try {
            await deleteDoc(doc(db, 'appNotifications', id));
            setNotifications(prev => prev.filter(n => n.id !== id));
            setSuccess("Notification deleted successfully.");
        } catch (err) {
            console.error("Error deleting notification:", err);
            setError("Failed to delete notification.");
        }
        setTimeout(() => setSuccess(null), 3000);
    };

    // Handler to toggle isActive status
    const handleToggleActive = async (notification) => {
        try {
            const notificationDocRef = doc(db, 'appNotifications', notification.id);
            const newStatus = !notification.isActive;
            
            await updateDoc(notificationDocRef, {
                isActive: newStatus
            });
            
            // Update local state immediately
            setNotifications(prev => prev.map(n => 
                n.id === notification.id ? { ...n, isActive: newStatus } : n
            ));
            
            setSuccess(`Notification successfully set to ${newStatus ? 'ACTIVE' : 'INACTIVE'}.`);
        } catch (err) {
            console.error("Error toggling active status:", err);
            setError("Failed to toggle notification status.");
        }
        setTimeout(() => setSuccess(null), 3000);
    };

    return (
        <div className="admin-page notifications-manager">
            <h1>📣 Notifications Manager</h1>
            
            {/* Notifications and Errors */}
            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            {/* --- 1. Add New Notification Form --- */}
            <div className="card new-notification-form">
                <h2><FaPlusCircle /> Post New Announcement</h2>
                <form onSubmit={handleAddNotification}>
                    <div className="form-group">
                        <label htmlFor="type">Notification Type</label>
                        <select 
                            id="type" 
                            value={newType} 
                            onChange={(e) => setNewType(e.target.value)}
                            disabled={isSubmitting}
                        >
                            {NOTIFICATION_TYPES.map(type => (
                                <option key={type} value={type}>
                                    {NOTIFICATION_ICONS[type] || ''} {type}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Placeholder for future Target Group/Expiry fields */}
                    <div className="form-group">
                        <label htmlFor="target">Target Group (Optional)</label>
                        <select id="target" disabled={isSubmitting}>
                            <option value="all">All Users</option>
                            <option value="premium">Premium Users</option>
                            <option value="free">Free Users</option>
                        </select>
                        <small>Default: All Users</small>
                    </div>

                    <div className="form-group full-width">
                        <label htmlFor="message">Message Content (Max 200 chars)</label>
                        <textarea
                            id="message"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            maxLength={200}
                            rows={3}
                            placeholder="Enter the announcement or new feature details here..."
                            disabled={isSubmitting}
                            required
                        />
                        <small>{newMessage.length} / 200 characters</small>
                    </div>

                    <button type="submit" disabled={isSubmitting || !newMessage.trim()}>
                        {isSubmitting ? 'Posting...' : 'Publish Notification'}
                    </button>
                </form>
            </div>

            {/* --- 2. Existing Notifications List --- */}
            <div className="card existing-notifications notifications-list-section"> {/* Added wrapper for table */}
                <h2><FaEdit /> Existing Announcements ({notifications.length})</h2>
                {loading ? (
                    <p>Loading existing notifications...</p>
                ) : notifications.length === 0 ? (
                    <p>No notifications have been posted yet.</p>
                ) : (
                    <table className="notifications-table">
                        <thead>
                            <tr>
                                <th>Icon/Type</th>
                                <th>Message</th>
                                <th>Target</th> {/* For mobile card label */}
                                <th>Posted By</th>
                                <th>Date/Time</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notifications.map((n) => (
                                <tr key={n.id} className={n.isActive ? 'active-row' : 'inactive-row'}>
                                    {/* data-label attributes added for mobile card view */}
                                    <td data-label="Type">{n.icon} {n.type}</td>
                                    <td data-label="Message">{n.message}</td>
                                    <td data-label="Target">All Users</td> {/* Placeholder for Target */}
                                    <td data-label="Posted By">{n.postedBy.split('@')[0]}</td>
                                    <td data-label="Date/Time">{n.timestamp}</td>
                                    <td data-label="Status">
                                        <span className={`status-badge ${n.isActive ? 'active' : 'inactive'}`}>
                                            {n.isActive ? 'ACTIVE' : 'INACTIVE'}
                                        </span>
                                    </td>
                                    <td data-label="Actions" className="actions-cell">
                                        <button 
                                            className={`toggle-btn ${n.isActive ? 'toggle-off' : 'toggle-on'}`}
                                            onClick={() => handleToggleActive(n)}
                                            title={n.isActive ? "Deactivate" : "Activate"}
                                        >
                                            {n.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                        </button>
                                        <button 
                                            className="delete-btn"
                                            onClick={() => handleDelete(n.id)}
                                            title="Delete Permanently"
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default NotificationsManager;